import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { cleanWhatsAppNumber } from "@/lib/whatsapp";

// Single source of truth for the business WhatsApp number (fallback when DB is unreachable).
// DB key `site_settings.whatsapp_number` overrides this at runtime.
export const FALLBACK_WA = "00905545684231";

const sanitizeWa = (value: string | null | undefined) =>
  (value ?? FALLBACK_WA).replace(/[^\d+]/g, "") || FALLBACK_WA;


// Generic settings cache
type SettingsMap = Record<string, string>;
let cache: SettingsMap = {};
const subs = new Set<(s: SettingsMap) => void>();
let initialized = false;

const broadcast = (next: SettingsMap) => {
  cache = next;
  subs.forEach((cb) => cb(next));
};

const loadAll = async () => {
  const { data } = await supabase.from("site_settings").select("key, value");
  const map: SettingsMap = {};
  (data ?? []).forEach((r) => { map[r.key] = r.value ?? ""; });
  broadcast(map);
};

const ensureInit = () => {
  if (initialized) return;
  initialized = true;
  void loadAll();
  supabase
    .channel("site-settings-all")
    .on("postgres_changes", { event: "*", schema: "public", table: "site_settings" }, () => void loadAll())
    .subscribe();
};

export const useSiteSettings = () => {
  const [settings, setSettings] = useState<SettingsMap>(cache);
  useEffect(() => {
    ensureInit();
    subs.add(setSettings);
    if (Object.keys(cache).length) setSettings(cache);
    return () => { subs.delete(setSettings); };
  }, []);
  return settings;
};

export const useSetting = (key: string, fallback = "") => {
  const all = useSiteSettings();
  return all[key] ?? fallback;
};

export const fetchSetting = async (key: string, fallback = ""): Promise<string> => {
  if (cache[key] != null) return cache[key];
  const { data } = await supabase.from("site_settings").select("value").eq("key", key).maybeSingle();
  return (data?.value as string | null) ?? fallback;
};

// WhatsApp helper kept for backwards compatibility
export const useWhatsApp = () => {
  const all = useSiteSettings();
  const stored = sanitizeWa(all["whatsapp_number"]);
  // wa.me requires international format WITHOUT leading zeros/+ to open the conversation directly.
  const digits = cleanWhatsAppNumber(stored);
  return {
    number: stored,
    waUrl: `https://wa.me/${digits}`,
    buildWaUrl: (text: string) => `https://wa.me/${digits}?text=${encodeURIComponent(text)}`,
  };
};


export const useNotificationEmail = () => ({ email: useSetting("admin_notification_email", "") });

export const fetchNotificationEmail = () => fetchSetting("admin_notification_email", "");

export const updateSetting = async (key: string, value: string, userId: string) => {
  const { error } = await supabase
    .from("site_settings")
    .upsert({ key, value, updated_by: userId, updated_at: new Date().toISOString() }, { onConflict: "key" });
  if (!error) broadcast({ ...cache, [key]: value });
  return { error };
};

export const updateWhatsAppNumber = (value: string, userId: string) =>
  updateSetting("whatsapp_number", sanitizeWa(value), userId);

export const updateNotificationEmail = (value: string, userId: string) =>
  updateSetting("admin_notification_email", value.trim(), userId);
